import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Gear, Globe, Palette, ShareNetwork, GoogleLogo, Robot, Envelope, ShieldCheck, FloppyDisk
} from '@phosphor-icons/react';

const SETTING_GROUPS = [
  { key: 'general', label: 'General', icon: Globe },
  { key: 'appearance', label: 'Appearance', icon: Palette },
  { key: 'social', label: 'Social Media', icon: ShareNetwork },
  { key: 'google', label: 'Google Services', icon: GoogleLogo },
  { key: 'ai', label: 'AI Features', icon: Robot },
  { key: 'email', label: 'Email', icon: Envelope },
  { key: 'security', label: 'Security', icon: ShieldCheck },
];

export default function CmsSiteSettings() {
  const [settings, setSettings] = useState(null);
  const [activeGroup, setActiveGroup] = useState('general');
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (settings && settings[activeGroup]) {
      setForm(settings[activeGroup]);
    }
  }, [activeGroup, settings]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/settings`, {
        withCredentials: true,
      });
      setSettings(r.data);
      setForm(r.data.general || {});
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/settings/${activeGroup}`,
        { data: form },
        { withCredentials: true }
      );
      toast.success('Settings saved');
      loadSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const testEmail = async () => {
    setTestingEmail(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/settings/test-email`,
        {},
        { withCredentials: true }
      );
      if (r.data.success) {
        toast.success(r.data.message || 'Test email sent');
      } else {
        toast.warning(r.data.message || 'Email test failed');
      }
    } catch (e) {
      toast.error('Email test failed');
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cms-site-settings">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Gear size={28} weight="duotone" className="text-primary" />
          Site Settings
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configure global site options, appearance, integrations, and security
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="md:col-span-1">
          <div className="bg-card border border-border rounded-lg p-2 space-y-1">
            {SETTING_GROUPS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveGroup(key)}
                data-testid={`settings-tab-${key}`}
                className={`w-full text-left inline-flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${activeGroup === key ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
              >
                <Icon size={16} weight={activeGroup === key ? 'duotone' : 'regular'} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="md:col-span-3">
          <form onSubmit={save} className="bg-card border border-border rounded-lg p-6 space-y-5" data-testid="settings-form">
            {/* General */}
            {activeGroup === 'general' && (
              <>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Site Name</label>
                  <input
                    value={form.site_name || ''}
                    onChange={(e) => setForm({ ...form, site_name: e.target.value })}
                    className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary"
                    data-testid="field-site_name"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Tagline</label>
                  <input
                    value={form.tagline || ''}
                    onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                    className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary"
                    data-testid="field-tagline"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Logo URL</label>
                    <input
                      value={form.logo_url || ''}
                      onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                      className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary"
                      data-testid="field-logo_url"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Favicon URL</label>
                    <input
                      value={form.favicon_url || ''}
                      onChange={(e) => setForm({ ...form, favicon_url: e.target.value })}
                      className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary"
                      data-testid="field-favicon_url"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.maintenance_mode || false}
                    onChange={(e) => setForm({ ...form, maintenance_mode: e.target.checked })}
                    id="maintenance_mode"
                    data-testid="field-maintenance_mode"
                  />
                  <label htmlFor="maintenance_mode" className="text-sm">Enable Maintenance Mode</label>
                </div>
                {form.maintenance_mode && (
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Maintenance Message</label>
                    <textarea
                      value={form.maintenance_message || ''}
                      onChange={(e) => setForm({ ...form, maintenance_message: e.target.value })}
                      rows={2}
                      className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary"
                      data-testid="field-maintenance_message"
                    />
                  </div>
                )}
              </>
            )}

            {/* Appearance */}
            {activeGroup === 'appearance' && (
              <>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Primary Color</label>
                  <input
                    type="color"
                    value={form.primary_color || '#b0552b'}
                    onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                    className="w-20 h-10 border border-border rounded cursor-pointer"
                    data-testid="field-primary_color"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Heading Font</label>
                  <input
                    value={form.font_heading || ''}
                    onChange={(e) => setForm({ ...form, font_heading: e.target.value })}
                    className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary"
                    data-testid="field-font_heading"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Body Font</label>
                  <input
                    value={form.font_body || ''}
                    onChange={(e) => setForm({ ...form, font_body: e.target.value })}
                    className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary"
                    data-testid="field-font_body"
                  />
                </div>
              </>
            )}

            {/* Social Media */}
            {activeGroup === 'social' && (
              <>
                {['facebook', 'instagram', 'twitter', 'linkedin', 'youtube', 'telegram'].map((platform) => (
                  <div key={platform}>
                    <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block capitalize">{platform}</label>
                    <input
                      value={form[platform] || ''}
                      onChange={(e) => setForm({ ...form, [platform]: e.target.value })}
                      placeholder={`https://${platform}.com/...`}
                      className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary"
                      data-testid={`field-${platform}`}
                    />
                  </div>
                ))}
              </>
            )}

            {/* Google Services */}
            {activeGroup === 'google' && (
              <>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Google Analytics ID</label>
                  <input
                    value={form.analytics_id || ''}
                    onChange={(e) => setForm({ ...form, analytics_id: e.target.value })}
                    placeholder="G-XXXXXXXXXX"
                    className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary"
                    data-testid="field-analytics_id"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Search Console Verification</label>
                  <input
                    value={form.search_console_verification || ''}
                    onChange={(e) => setForm({ ...form, search_console_verification: e.target.value })}
                    className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary"
                    data-testid="field-search_console_verification"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Google Tag Manager ID</label>
                  <input
                    value={form.gtm_id || ''}
                    onChange={(e) => setForm({ ...form, gtm_id: e.target.value })}
                    placeholder="GTM-XXXXXX"
                    className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary"
                    data-testid="field-gtm_id"
                  />
                </div>
              </>
            )}

            {/* AI Features */}
            {activeGroup === 'ai' && (
              <>
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Enabled Features</p>
                  {['grammar_check', 'summarize', 'translate', 'author_analysis', 'author_chat', 'generate_tags', 'generate_titles'].map((feat) => (
                    <div key={feat} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={form.features?.[feat] ?? true}
                        onChange={(e) => setForm({ ...form, features: { ...form.features, [feat]: e.target.checked } })}
                        id={`ai-${feat}`}
                        data-testid={`field-ai-${feat}`}
                      />
                      <label htmlFor={`ai-${feat}`} className="text-sm capitalize">{feat.replace(/_/g, ' ')}</label>
                    </div>
                  ))}
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Default Model</label>
                  <select
                    value={form.default_model || 'gpt-5.4'}
                    onChange={(e) => setForm({ ...form, default_model: e.target.value })}
                    className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary"
                    data-testid="field-default_model"
                  >
                    <option value="gpt-5.4">OpenAI GPT-5.4</option>
                    <option value="claude-sonnet-4.6">Claude Sonnet 4.6</option>
                  </select>
                </div>
              </>
            )}

            {/* Email */}
            {activeGroup === 'email' && (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
                  <p className="font-semibold mb-1">⚠️ Resend Domain Verification</p>
                  <p>Current sender: <code className="bg-amber-100 px-1">{form.sender_email}</code></p>
                  {form.sender_email?.includes('resend.dev') && (
                    <p className="mt-2">You're using Resend sandbox. <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="underline">Verify your domain</a> to send to all users.</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.enabled ?? true}
                    onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                    id="email-enabled"
                    data-testid="field-email-enabled"
                  />
                  <label htmlFor="email-enabled" className="text-sm">Enable Email Sending</label>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Sender Email</label>
                  <input
                    value={form.sender_email || ''}
                    disabled
                    className="w-full text-sm px-3 py-2 bg-muted border border-border rounded outline-none"
                    data-testid="field-sender_email"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Configure via backend .env</p>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Sender Name</label>
                  <input
                    value={form.sender_name || ''}
                    disabled
                    className="w-full text-sm px-3 py-2 bg-muted border border-border rounded outline-none"
                    data-testid="field-sender_name"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Configure via backend .env</p>
                </div>
                <button
                  type="button"
                  onClick={testEmail}
                  disabled={testingEmail}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold disabled:opacity-50"
                  data-testid="test-email"
                >
                  {testingEmail ? 'Sending…' : 'Send Test Email'}
                </button>
              </>
            )}

            {/* Security */}
            {activeGroup === 'security' && (
              <>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Min Password Length</label>
                  <input
                    type="number"
                    value={form.password_min_length || 8}
                    onChange={(e) => setForm({ ...form, password_min_length: parseInt(e.target.value) })}
                    min="6"
                    max="32"
                    className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary"
                    data-testid="field-password_min_length"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Login Attempt Limit</label>
                  <input
                    type="number"
                    value={form.login_attempt_limit || 5}
                    onChange={(e) => setForm({ ...form, login_attempt_limit: parseInt(e.target.value) })}
                    className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary"
                    data-testid="field-login_attempt_limit"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Lockout Duration (minutes)</label>
                  <input
                    type="number"
                    value={form.lockout_minutes || 15}
                    onChange={(e) => setForm({ ...form, lockout_minutes: parseInt(e.target.value) })}
                    className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary"
                    data-testid="field-lockout_minutes"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.email_verification_required ?? true}
                    onChange={(e) => setForm({ ...form, email_verification_required: e.target.checked })}
                    id="email-verification-required"
                    data-testid="field-email_verification_required"
                  />
                  <label htmlFor="email-verification-required" className="text-sm">Require Email Verification</label>
                </div>
              </>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-border">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold text-sm disabled:opacity-50"
                data-testid="save-settings"
              >
                <FloppyDisk size={16} weight={saving ? 'bold' : 'regular'} />
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
